/**
 * Registre d'appartenance ARTICLE → CODE.
 *
 * Contexte (bug QA) : le panneau « Sources » a affiché
 * « Article L. 221-18 — Code de la construction » alors que l'article L. 221-18
 * (délai de rétractation) relève du Code de la consommation. Le libellé provient
 * du titre de la source ; lorsqu'il est produit ou reformulé par le modèle, il
 * peut nommer un code erroné.
 *
 * Principe retenu : liste blanche STRICTE et documentée de plages d'articles dont
 * l'appartenance à un code est certaine et exclusive. Hors de ces plages, on ne
 * corrige RIEN (on n'invente jamais un code). Chaque entrée doit rester
 * vérifiable sur Légifrance.
 */

export type ArticleCodeRange = {
  /** Préfixe normalisé de l'article, ex. "L.221". */
  prefix: string;
  /** Borne basse incluse du suffixe numérique. */
  from: number;
  /** Borne haute incluse du suffixe numérique. */
  to: number;
  /** Libellé officiel du code. */
  code: string;
};

/**
 * Plages connues. Une correction n'est appliquée QUE si le numéro d'article
 * relève d'une seule et unique plage : dès que plusieurs codes se partagent le
 * même numéro (ex. L. 121-1 existe au Code de la propriété intellectuelle, au
 * Code de la consommation et au Code de la route), l'appartenance est
 * indécidable et le code cité dans la réponse est conservé tel quel.
 */
export const ARTICLE_CODE_RANGES: ArticleCodeRange[] = [
  // Contrats conclus à distance et hors établissement (rétractation de 14 jours).
  // Le Code de la construction et de l'habitation utilise L. 221-1 à L. 221-9 :
  // la plage consommation démarre donc à 10.
  { prefix: "L.221", from: 10, to: 29, code: "Code de la consommation" },
  // Pratiques commerciales trompeuses.
  { prefix: "L.121", from: 1, to: 7, code: "Code de la consommation" },
  // Clauses abusives.
  { prefix: "L.212", from: 1, to: 3, code: "Code de la consommation" },

  // ── Plages concurrentes, déclarées pour NEUTRALISER toute correction ──
  // Propriété intellectuelle : droit moral (L. 121-1 s.) et durée de
  // protection (L. 123-1 s.). Recouvre volontairement la plage consommation
  // L.121 afin qu'aucun code ne soit imposé sur ces numéros.
  { prefix: "L.121", from: 1, to: 9, code: "Code de la propriété intellectuelle" },
  // Durée de protection (L. 123-1 s.) : le Code de commerce utilise aussi la
  // série L. 123 (registre du commerce), l'appartenance reste donc indécidable.
  { prefix: "L.123", from: 1, to: 12, code: "Code de la propriété intellectuelle" },
  { prefix: "L.123", from: 1, to: 12, code: "Code de commerce" },
  // Code de la route : responsabilité pécuniaire du titulaire (L. 121-1 s.).
  { prefix: "L.121", from: 1, to: 6, code: "Code de la route" },
];

/** Normalise « L. 221-18 », « l221-18 », « L.221‑18 » → « L.221-18 ». */
export function normalizeArticleRef(raw: string): string {
  return (raw || "")
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/\s+/g, "")
    .replace(/^([LRDA])\.?/i, (_m, l: string) => `${l.toUpperCase()}.`);
}

/**
 * Code auquel appartient un numéro d'article, ou `null` si l'appartenance
 * n'est pas certaine (cas très majoritaire : aucune correction n'est faite).
 * L'ambiguïté (plusieurs codes candidats) vaut incertitude.
 */
export function codeForArticle(rawNumber: string): string | null {
  const ref = normalizeArticleRef(rawNumber);
  const m = ref.match(/^([LRDA]\.\d+)-(\d+)/);
  if (!m) return null;
  const [, prefix, suffix] = m;
  const n = Number(suffix);
  const candidates = new Set(
    ARTICLE_CODE_RANGES.filter((r) => r.prefix === prefix && n >= r.from && n <= r.to).map((r) => r.code),
  );
  if (candidates.size !== 1) return null;
  return [...candidates][0];
}


/** Nom de code mentionné dans un libellé de source, s'il y en a un. */
const CODE_IN_TITLE_RE =
  /\b[Cc]ode\s+(?:civil|p[ée]nal|de\s+commerce|du\s+travail|de\s+la\s+consommation|de\s+la\s+construction(?:\s+et\s+de\s+l['’]habitation)?|de\s+la\s+sant[ée]\s+publique|de\s+la\s+s[ée]curit[ée]\s+sociale|de\s+l['’]environnement|de\s+l['’]urbanisme|de\s+la\s+propri[ée]t[ée]\s+intellectuelle|mon[ée]taire\s+et\s+financier|des\s+assurances|g[ée]n[ée]ral\s+des\s+imp[ôo]ts|de\s+proc[ée]dure\s+civile|de\s+proc[ée]dure\s+p[ée]nale)/;

const ARTICLE_IN_TITLE_RE = /\barticles?\s+((?:[LRDA]\.?\s*)?\d+[\u2010-\u2015-]\d+)/i;

/**
 * Corrige le nom de code d'un libellé de source lorsqu'il contredit le registre.
 * Retourne le libellé inchangé dans tous les autres cas.
 */
export function correctCodeLabel(title: string): string {
  if (!title) return title;
  const article = title.match(ARTICLE_IN_TITLE_RE);
  if (!article) return title;
  const expected = codeForArticle(article[1]);
  if (!expected) return title;
  const named = title.match(CODE_IN_TITLE_RE);
  if (!named) return title;
  const normalize = (s: string) => s.toLowerCase().replace(/['’]/g, "'").replace(/\s+/g, " ");
  if (normalize(named[0]).startsWith(normalize(expected))) return title;
  return title.replace(CODE_IN_TITLE_RE, expected);
}
